import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import {
  CountriesCollection,
  Education,
  EducationsCollection,
  UniversitiesCollection,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { Margin } from '@freelancer/ui/margin';
import { SelectAcceptedType } from '@freelancer/ui/select';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { maxLength, required } from '@freelancer/ui/validators';
import * as Rx from 'rxjs';
import { map, startWith, take, toArray } from 'rxjs/operators';

enum EducationFormInput {
  COUNTRY = 'country',
  INSTITUTION = 'institution',
  DEGREE = 'degree',
  START_YEAR = 'start_year',
  END_YEAR = 'end_year',
}

@Component({
  selector: 'app-user-profile-educations-edit',
  template: `
    <fl-grid>
      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text
          i18n="Education country select label"
          [weight]="FontWeight.BOLD"
        >
          Country
        </fl-text>
        <fl-select
          flTrackingLabel="EducationEditCountrySelect"
          [control]="countryControl"
          [disabled]="loadingCountries$ | flAsync"
          [options]="countriesList$ | flAsync"
          placeholder="Select country"
          i18n-placeholder="Country select input"
        ></fl-select>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text
          i18n="Education institution select label"
          [weight]="FontWeight.BOLD"
        >
          University/College
        </fl-text>
        <fl-select
          flTrackingLabel="EducationEditUniversitySelect"
          placeholder="Select university/college"
          i18n-placeholder="university/college select input"
          [control]="institutionControl"
          [disabled]="countryControl.invalid"
          [options]="(universities$ | flAsync) || []"
        ></fl-select>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-text
          i18n="Education institution select label"
          [weight]="FontWeight.BOLD"
        >
          Degree
        </fl-text>
        <fl-input
          flTrackingLabel="EducationEditDegree"
          placeholder="Enter your degree"
          i18n-placeholder="Degree input placeholder"
          [control]="degreeControl"
        ></fl-input>
      </fl-col>

      <fl-col
        [col]="12"
        [colDesktopSmall]="3"
        [flMarginBottom]="Margin.XXSMALL"
      >
        <fl-text
          i18n="Education institution select label"
          [weight]="FontWeight.BOLD"
        >
          Start year
        </fl-text>
        <fl-select
          flTrackingLabel="EducationEditStartYear"
          placeholder="Select year"
          i18n-placeholder="Start year input placeholder"
          [control]="startYearControl"
          [options]="yearsList$ | flAsync"
        ></fl-select>
      </fl-col>

      <fl-col
        [col]="12"
        [colDesktopSmall]="3"
        [flMarginBottom]="Margin.XXSMALL"
      >
        <fl-text
          i18n="Education institution select label"
          [weight]="FontWeight.BOLD"
        >
          End year
        </fl-text>
        <fl-select
          flTrackingLabel="EducationEditEndYear"
          placeholder="Select year"
          i18n-placeholder="End year input placeholder"
          [control]="endYearControl"
          [options]="yearsList$ | flAsync"
        ></fl-select>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-text
          *ngIf="showInvalidDateMessage$ | flAsync"
          i18n="Invalid end date error message"
          [color]="FontColor.ERROR"
          [size]="TextSize.XXSMALL"
        >
          The end year must be after or equal to the start year.
        </fl-text>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-banner-alert
          *ngIf="isError$ | flAsync"
          i18n="Error message label"
          [closeable]="false"
          [type]="BannerAlertType.ERROR"
        >
          Oops, something went wrong when saving your education item. Please try
          again.
        </fl-banner-alert>
      </fl-col>

      <fl-col
        *ngIf="needsDeleteConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to delete this education?"
          flTrackingLabel="UserProfileEducationEditDelete"
          i18n-body="Delete education confirmation body"
          i18n-title="Delete education confirmation title"
          title="Delete Education"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDeleteEducation()"
          (confirm)="handleConfirmDeleteEducation()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col
        *ngIf="needsCancelConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to discard any changes?"
          flTrackingLabel="UserProfileEducationEditDiscardChanges"
          i18n-body="Discard changes confirmation body"
          i18n-title="Discard changes confirmation title"
          title="Discard Changes"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDiscardChanges()"
          (confirm)="handleConfirmDiscardChanges()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col *ngIf="!(hideActionRow$ | flAsync)" [col]="12">
        <app-user-profile-editable-ui-action-row
          deleteLinkText="Delete Education"
          flTrackingLabel="UserProfileEducationsEdit"
          i18n-deleteLinkText="Delete Education link label"
          [disabled]="saveDisabled$ | flAsync"
          [isBusy]="isBusy$ | flAsync"
          [showDeleteButton]="!!(currentEducationId$ | flAsync)"
          (cancel)="handleCancel()"
          (delete)="handleDeleteEducation()"
          (save)="handleSave()"
        >
        </app-user-profile-editable-ui-action-row>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./user-profile-educations-edit.component.scss'],
})
export class UserProfileEducationsEditComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  FontColor = FontColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  Margin = Margin;

  countryControl = new FormControl('', [
    required($localize`Please select a country.`),
  ]);
  institutionControl = new FormControl('', [
    required($localize`Please select an institution.`),
  ]);
  degreeControl = new FormControl('', [
    required($localize`Please enter a degree.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);
  startYearControl = new FormControl(undefined, [
    required($localize`Please enter a start date.`),
  ]);
  endYearControl = new FormControl(undefined, [
    required($localize`Please enter an end date.`),
  ]);

  portfolioForm = new FormGroup({
    [EducationFormInput.COUNTRY]: this.countryControl,
    [EducationFormInput.INSTITUTION]: this.institutionControl,
    [EducationFormInput.DEGREE]: this.degreeControl,
    [EducationFormInput.START_YEAR]: this.startYearControl,
    [EducationFormInput.END_YEAR]: this.endYearControl,
  });

  @Input() education$: Rx.Observable<Education | undefined>;
  @Input() educationsCollection: DatastoreCollection<EducationsCollection>;
  // Draft educations are educations not saved to the backend.
  // Do not update datastore collection with draft educations. Return it with modified fields.
  @Input() isDraftEducation = false;
  @Output() cancel = new EventEmitter<void>();
  @Output() updateDraftEducation = new EventEmitter<Education>();
  @Output() newDraftEducation = new EventEmitter<Education>();

  countriesCollection$: DatastoreCollection<CountriesCollection>;
  universitiesCollection$: DatastoreCollection<UniversitiesCollection>;

  private _isBusy$ = new Rx.BehaviorSubject<boolean>(false);
  isBusy$ = this._isBusy$.asObservable();

  private _isError$ = new Rx.BehaviorSubject<boolean>(false);
  isError$ = this._isError$.asObservable();

  private _needsDeleteConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsDeleteConfirmation$ = this._needsDeleteConfirmation$.asObservable();

  private _needsCancelConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsCancelConfirmation$ = this._needsCancelConfirmation$.asObservable();

  // If this is set the user is currently editing a education and if it is
  // undefined the user is creating a new education item.
  // If this is a negative number the user is editing a draft(unpushed) education item.
  private _currentEducationId$ = new Rx.BehaviorSubject<number | undefined>(
    undefined,
  );
  currentEducationId$ = this._currentEducationId$.asObservable();

  educationSubscription?: Rx.Subscription;
  hideActionRow$: Rx.Observable<boolean>;
  loadingCountries$: Rx.Observable<boolean>;
  saveDisabled$: Rx.Observable<boolean>;
  validDateSelected$: Rx.Observable<boolean>;
  showInvalidDateMessage$: Rx.Observable<boolean>;

  countriesList$: Rx.Observable<readonly SelectAcceptedType[]>;
  universities$: Rx.Observable<readonly SelectAcceptedType[]>;
  yearsList$: Rx.Observable<readonly SelectAcceptedType[]>;

  constructor(private datastore: Datastore) {}

  ngOnInit() {
    this.educationSubscription = this.education$.subscribe(education => {
      // If the education is undefined then we are adding a new education.
      if (!education) {
        this._currentEducationId$.next(undefined);
        return;
      }

      this._currentEducationId$.next(education.id);
      this.degreeControl.setValue(education.degree);
      this.institutionControl.setValue(education.schoolId ?? '');
      this.countryControl.setValue(education.countryCode ?? '');

      const startYear = education.startDate
        ? new Date(education.startDate).getFullYear()
        : undefined;
      const endYear = education.endDate
        ? new Date(education.endDate).getFullYear()
        : undefined;

      this.startYearControl.setValue(startYear);
      this.endYearControl.setValue(endYear);
    });

    this.hideActionRow$ = Rx.combineLatest([
      this._needsDeleteConfirmation$,
      this._needsCancelConfirmation$,
    ]).pipe(
      map(([deleteConfirm, cancelConfirm]) => deleteConfirm || cancelConfirm),
    );

    this.countriesCollection$ = this.datastore.collection<CountriesCollection>(
      'countries',
    );

    this.loadingCountries$ = this.countriesCollection$.status$.pipe(
      map(status => !status.ready),
    );

    this.countriesList$ = this.countriesCollection$.valueChanges().pipe(
      map(countries =>
        countries.map(country => ({
          value: country.code,
          displayText: country.name,
        })),
      ),
      // Append an empty string to show the placeholder text in the select bit.
      map(countries => ['', ...countries]),
    );

    this.yearsList$ = this.getListOfYears(
      1900,
      new Date().getFullYear() + 1,
    ).pipe(
      map(years =>
        years
          .map(year => ({
            displayText: year.toString(),
            value: year,
          }))
          .reverse(),
      ),
      // This is needed as to show the placeholder text.
      map(years => ['', ...years]),
    );

    this.universitiesCollection$ = this.datastore.collection<UniversitiesCollection>(
      'universities',
      query =>
        Rx.combineLatest([
          this.countryControl.valueChanges.pipe(startWith(undefined)),
          this.education$.pipe(startWith(undefined)),
        ]).pipe(
          map(([countryControl, education]) =>
            // If the user has selected a country filter the universities with
            // that. If not check if an education is currently being edited and
            // if it is take the country from that.
            countryControl
              ? query.where('countryCode', 'equalsIgnoreCase', countryControl)
              : education && education.countryCode
              ? query.where(
                  'countryCode',
                  'equalsIgnoreCase',
                  education.countryCode,
                )
              : query.null(),
          ),
        ),
    );

    this.universities$ = this.universitiesCollection$.valueChanges().pipe(
      map(universities => [
        // This allows the placeholder text to be shown.
        '',
        ...universities
          .map(university => ({
            value: university.id,
            displayText: university.name,
          }))
          .sort((a, b) => {
            if (a.displayText.toLowerCase() < b.displayText.toLowerCase()) {
              return -1;
            }
            if (a.displayText.toLowerCase() > b.displayText.toLowerCase()) {
              return 1;
            }
            return 0;
          }),
      ]),
      map(mappedUniversities =>
        mappedUniversities.length > 1
          ? mappedUniversities
          : [
              ...mappedUniversities,
              { value: 0, displayText: $localize`Others...` },
            ],
      ),
    );

    this.validDateSelected$ = Rx.combineLatest([
      this.startYearControl.valueChanges.pipe(startWith(undefined)),
      this.endYearControl.valueChanges.pipe(startWith(undefined)),
    ]).pipe(
      map(() => this.startYearControl.value <= this.endYearControl.value),
    );

    this.showInvalidDateMessage$ = this.validDateSelected$.pipe(
      map(validDate => this.endYearControl.value && !validDate),
    );

    this.saveDisabled$ = Rx.combineLatest([
      this.portfolioForm.valueChanges.pipe(startWith(undefined)),
      this.validDateSelected$,
    ]).pipe(map(([_, validDate]) => !this.portfolioForm.valid || !validDate));
  }

  handleSave(): void {
    const educationId = this._currentEducationId$.getValue();
    // If we have a education id the education already exists so we need to
    // update it instead of create a new one.
    if (educationId) {
      this.updateEducation(educationId);
    } else {
      this.createNewEducation();
    }
  }

  /**
   * Returns a list of years between the given years.
   *
   * @param start Start year
   * @param end End year
   */
  getListOfYears(start: number, end: number): Rx.Observable<readonly number[]> {
    return Rx.range(start, end - start).pipe(take(end - start), toArray());
  }

  async createNewEducation(): Promise<void> {
    this._isBusy$.next(true);

    const startDate = Date.UTC(this.startYearControl.value, 0);
    const endDate = Date.UTC(this.endYearControl.value, 0);

    const newEducation: Education = {
      countryCode: this.countryControl.value,
      degree: this.degreeControl.value,
      schoolId: this.institutionControl.value,
      startDate,
      endDate,
      userId: -1, // Needs the userId set. Not actually used in request.
      id: -Date.now(), // used to distinguish draft education, not actually used in request
      ordering: 0, // Unused in the request.
    };

    if (this.isDraftEducation) {
      const countryName = await this.countriesCollection$
        .valueChanges()
        .pipe(
          take(1),
          map(countries =>
            countries.find(
              country => country.code === this.countryControl.value,
            ),
          ),
          map(country => country?.name),
        )
        .toPromise();

      const schoolName = await this.universitiesCollection$
        .valueChanges()
        .pipe(
          take(1),
          map(universities =>
            universities.find(
              university => university.id === this.institutionControl.value,
            ),
          ),
          map(university => university?.name),
        )
        .toPromise();

      this.newDraftEducation.emit({
        ...newEducation,
        // country, schoolName and duration is for displaying draft education only
        country: countryName,
        schoolName,
        duration: startDate && endDate ? endDate - startDate : undefined,
      });
      this._isBusy$.next(false);
      this.cancel.emit();
    } else {
      this.educationsCollection.push(newEducation).then(response => {
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
          return;
        }

        // Cancel out of the edit view on success.
        this.cancel.emit();
      });
    }
  }

  async updateEducation(educationId: number): Promise<void> {
    this._isBusy$.next(true);

    const startDate = Date.UTC(this.startYearControl.value, 0);
    const endDate = Date.UTC(this.endYearControl.value, 0);

    const educationFields = {
      countryCode: this.countryControl.value,
      degree: this.degreeControl.value,
      schoolId: this.institutionControl.value,
      startDate,
      endDate,
    };

    if (this.isDraftEducation) {
      const countryName = await this.countriesCollection$
        .valueChanges()
        .pipe(
          take(1),
          map(countries =>
            countries.find(
              country => country.code === this.countryControl.value,
            ),
          ),
          map(country => country?.name),
        )
        .toPromise();

      const schoolName = await this.universitiesCollection$
        .valueChanges()
        .pipe(
          take(1),
          map(universities =>
            universities.find(
              university => university.id === this.institutionControl.value,
            ),
          ),
          map(university => university?.name),
        )
        .toPromise();

      const education: Education = {
        ...educationFields,
        // country, schooname and duration is for displaying draft education only
        country: countryName,
        schoolName,
        duration: startDate && endDate ? endDate - startDate : undefined,
        id: educationId,
        userId: -1, // Needs the userId set. Not actually used in request.
        ordering: 0, // Unused in the request.
      };

      this._isBusy$.next(false);
      this.updateDraftEducation.emit(education);

      // Cancel out of the edit view on success.
      this.cancel.emit();
    } else {
      this.educationsCollection
        .update(educationId, educationFields)
        .then(response => {
          this._isBusy$.next(false);

          if (response.status === 'error') {
            this._isError$.next(true);
            return;
          }

          // Cancel out of the edit view on success.
          this.cancel.emit();
        });
    }
  }

  handleConfirmDeleteEducation(): void {
    this._isBusy$.next(true);
    const educationId = this._currentEducationId$.getValue();
    // If this is not defined we should not be able to delete the education.
    if (!educationId) {
      return;
    }

    this.educationsCollection.remove(educationId).then(response => {
      this._needsDeleteConfirmation$.next(false);
      this._isBusy$.next(false);

      if (response.status === 'error') {
        this._isError$.next(true);
        return;
      }

      // Cancel out of the edit view on success.
      this.cancel.emit();
    });
  }

  handleCancelDeleteEducation(): void {
    this._needsDeleteConfirmation$.next(false);
  }

  handleCancelDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
  }

  handleConfirmDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
    this.cancel.emit();
  }

  handleCancel(): void {
    if (this.portfolioForm.dirty) {
      this._needsCancelConfirmation$.next(true);
      return;
    }
    this.cancel.emit();
  }

  handleDeleteEducation(): void {
    this._needsDeleteConfirmation$.next(true);
  }

  ngOnDestroy(): void {
    if (this.educationSubscription) {
      this.educationSubscription.unsubscribe();
    }
  }
}
